import axios from 'axios';
import config from 'commons-general/config/index.js';
import { HTTPService } from '@sm360/widget-commons-core-services';
import moment from 'moment';

import CookieService from 'commons-general/services/CookieService/CookieService.js';
import UtilsService from 'commons-general/services/UtilsService/UtilsService.js';
import COOKIES from 'commons-general/constants/cookies.js';
import ERRORS from 'commons-general/constants/errors.js';

/**
 * Get informations and do actions on accounts
 */
class AccountService {
    constructor() {
        this.accountBasePath = `${config.endpoint.account}/accounts`;
    }

    /**
     * Let know if someone is authenticated or not
     * @return {boolean} authenticated or not
     */
    isAuthenticated() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);

        if (!token || token === 'null' || token === 'undefined') {
            return false;
        }

        const extractedToken = UtilsService.extractTokenPayload(token);

        const isAuthenticated =
            !!(extractedToken && extractedToken.exp) && moment().isSameOrBefore(moment(extractedToken.exp, 'X'));

        if (!isAuthenticated) {
            CookieService.deleteToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
            CookieService.deleteToken(COOKIES.ACCOUNT.REFRESH_TOKEN);
        }

        return isAuthenticated;
    }

    /**
     * Make http account to get account informations
     * @return {AccountRecord} Account informations
     */
    fetchAccount() {
        const accountToken = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);

        return axios
            .get(this.accountBasePath, {
                headers: {
                    'x-auth-token': accountToken,
                },
            })
            .then((response) => {
                if (response.status === 204) {
                    return Promise.reject(
                        HTTPService.formatRejectResponse(ERRORS.UNKNOW_ERROR_FETCHING_PROFILE, response)
                    );
                }

                this.account = response.data;

                return Promise.resolve(this.account);
            });
    }

    /**
     * Get account informations saved in memory
     * If there's no account in memory it calls {@link fetchAccount}
     * @return {AccountRecord} Account informations
     */
    getLoggedAccount() {
        if (!this.isAuthenticated()) {
            return Promise.reject(HTTPService.formatRejectResponse('notAuthenticated'));
        }

        if (!this.account) {
            return this.fetchAccount();
        }

        return Promise.resolve(this.account);
    }

    /**
     * Confirm email's account
     * @param {string} username - this is the email
     * @param {string} validationKey - this is the key to validate the email
     * @param {number} id - this is account id
     * @return {Promise} Results of the http call
     */
    confirmEmail(username, validationKey, id) {
        const url = id ? `${this.accountBasePath}/v2/validate` : `${this.accountBasePath}/validate`;

        const data = {
            username,
            validationKey,
            id,
        };

        return axios.put(url, data);
    }

    /**
     * Get AccountId
     * @param {string} token - token user
     * @return {string} return accountId
     */
    getAccountId(token) {
        const extractedToken = UtilsService.extractTokenPayload(token);
        return (extractedToken && extractedToken.ACCOUNT_ID) || null;
    }

    /**
     * Get User AccountId
     * @return {string} return accountId
     */
    getUserAccountId() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        return this.getAccountId(token);
    }
}

export default new AccountService();
